<template>
  <div class="chat-window">
    <!-- Header -->
    <div class="chat-window__header">
      <v-btn v-if="showBack" icon @click="$emit('back')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-avatar size="40" color="grey lighten-3" class="mr-3">
        <img v-if="partnerAvatar" :src="partnerAvatar" :alt="partnerName" />
        <v-icon v-else color="grey">mdi-account</v-icon>
      </v-avatar>
      <div class="chat-window__header-info">
        <span class="chat-window__header-name">{{ partnerName }}</span>
        <span v-if="isTyping" class="chat-window__typing">กำลังพิมพ์...</span>
        <span v-else-if="partnerOnline" class="chat-window__online">ออนไลน์</span>
        <span v-else class="chat-window__offline">ออฟไลน์</span>
      </div>
    </div>

    <!-- Messages Area -->
    <div ref="messagesArea" class="chat-window__messages" @scroll="onScroll">
      <!-- Load More -->
      <div v-if="loadingMore" class="chat-window__loading">
        <v-progress-circular indeterminate size="24" color="purple" />
      </div>

      <!-- Date separators and messages -->
      <div
        v-for="item in flatMessageList"
        :key="item.key"
        :class="item.type === 'date' ? 'chat-window__date-separator' : 'chat-window__bubble-wrap'"
      >
        <span v-if="item.type === 'date'">{{ item.label }}</span>
        <ChatBubble
          v-if="item.type === 'msg'"
          :message="item.message"
          :is-mine="item.message.senderRole === myRole"
        />
      </div>

      <!-- Empty State -->
      <div v-if="!loading && messages.length === 0" class="chat-window__empty">
        <v-icon size="64" color="grey lighten-2">mdi-message-text-outline</v-icon>
        <p>เริ่มการสนทนา</p>
      </div>

      <!-- Typing Indicator Bubble -->
      <div v-if="isTyping" class="chat-window__typing-wrap">
        <div class="chat-window__typing-bubble">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="chat-window__input-area">
      <v-text-field
        v-model="newMessage"
        placeholder="พิมพ์ข้อความ..."
        outlined
        dense
        hide-details
        class="chat-window__input"
        @keydown.enter.prevent="sendMessage"
        @input="onTyping"
      >
        <template #append>
          <v-btn icon small @click="sendMessage" :disabled="!canSend" color="primary">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </div>
</template>

<script>
import ChatBubble from "./ChatBubble.vue";
import { getMessagesApi, sendMessageApi, markAsReadApi } from "@/services/api/chat";

export default {
  name: "ChatWindow",
  components: { ChatBubble },
  props: {
    conversationId: { type: String, default: "" },
    partnerName: { type: String, default: "" },
    partnerAvatar: { type: String, default: "" },
    partnerOnline: { type: Boolean, default: false },
    myRole: { type: String, default: "user" },
    showBack: { type: Boolean, default: false },
  },
  data() {
    return {
      messages: [],
      newMessage: "",
      loading: false,
      loadingMore: false,
      hasMore: false,
      nextCursor: null,
      isTyping: false,
      typingTimeout: null,
      isEmittingTyping: false,
    };
  },
  computed: {
    canSend() {
      return this.newMessage.trim().length > 0 && this.conversationId;
    },
    flatMessageList() {
      if (!this.messages.length) return [];
      const items = [];
      let currentDate = "";
      let dateIdx = 0;
      for (const msg of this.messages) {
        const d = new Date(msg.createdAt);
        const dateStr = d.toDateString();
        if (dateStr !== currentDate) {
          currentDate = dateStr;
          const now = new Date();
          let label;
          if (dateStr === now.toDateString()) {
            label = "วันนี้";
          } else {
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            if (dateStr === yesterday.toDateString()) {
              label = "เมื่อวาน";
            } else {
              label = d.toLocaleDateString("th-TH", {
                day: "numeric",
                month: "long",
                year: "numeric",
              });
            }
          }
          items.push({ type: "date", key: "date-" + dateIdx++, label });
        }
        items.push({ type: "msg", key: "msg-" + msg.id, message: msg });
      }
      return items;
    },
  },
  watch: {
    conversationId: {
      handler(val) {
        if (val) {
          this.initConversation();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setupSocketListeners();
    if (!this.$socket.connected) {
      console.warn("[ChatWindow] Socket is disconnected! Proceeding to force reconnect...");
      this.reconnectSocketFallback();
    }
  },
  beforeDestroy() {
    this.cleanupSocket();
  },
  methods: {
    async initConversation() {
      if (!this.conversationId) return;

      // Leave previous room
      this.$socket.emit("chat:leave", this._prevConversationId);
      this._prevConversationId = this.conversationId;

      // Join new room
      this.$socket.emit("chat:join", this.conversationId);

      // Reset state
      this.messages = [];
      this.hasMore = false;
      this.nextCursor = null;
      this.isTyping = false;

      // Load messages
      await this.loadMessages();

      // Mark as read
      this.markAsRead();

      // Scroll to bottom
      this.$nextTick(() => this.scrollToBottom());
    },

    async loadMessages(loadMore = false) {
      if (loadMore) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }

      try {
        const paramsObj = { limit: 30 };
        if (this.nextCursor) paramsObj.cursor = this.nextCursor;

        const data = await getMessagesApi(this.myRole, this.conversationId, paramsObj);

        const { messages, hasMore, nextCursor } = data;

        if (loadMore) {
          // Prepend older messages
          const area = this.$refs.messagesArea;
          const oldScrollHeight = area?.scrollHeight || 0;
          this.messages = [...messages, ...this.messages];
          this.$nextTick(() => {
            if (area) area.scrollTop = area.scrollHeight - oldScrollHeight;
          });
        } else {
          this.messages = messages;
        }

        this.hasMore = hasMore;
        this.nextCursor = nextCursor;
      } catch (err) {
        console.error("loadMessages error:", err);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async sendMessage() {
      if (!this.canSend) return;

      const content = this.newMessage.trim();
      this.newMessage = "";

      // Optimistic update
      const tempMsg = {
        id: "temp-" + Date.now(),
        content,
        senderRole: this.myRole,
        isRead: false,
        createdAt: new Date().toISOString(),
      };
      this.messages.push(tempMsg);
      this.$nextTick(() => this.scrollToBottom());

      // Stop typing indicator
      this.$socket.emit("chat:stopTyping", {
        conversationId: this.conversationId,
        senderRole: this.myRole,
      });

      try {
        const data = await sendMessageApi(this.myRole, this.conversationId, { content });

        // Replace temp message with real one
        const idx = this.messages.findIndex((m) => m.id === tempMsg.id);
        if (idx !== -1) {
          this.$set(this.messages, idx, data);
        }
      } catch (err) {
        console.error("sendMessage error:", err);
        // Remove temp message on error
        this.messages = this.messages.filter((m) => m.id !== tempMsg.id);
      }
    },

    markAsRead() {
      if (!this.conversationId) return;
      markAsReadApi(this.myRole, this.conversationId).catch((err) =>
        console.error("markAsRead error:", err)
      );
    },

    setupSocketListeners() {
      this.$socket.on("connect", this.onSocketConnect);
      this.$socket.on("chat:newMessage", this.onNewMessage);
      this.$socket.on("chat:messagesRead", this.onMessagesRead);
      this.$socket.on("chat:typing", this.onPartnerTyping);
      this.$socket.on("chat:stopTyping", this.onPartnerStopTyping);
    },

    cleanupSocket() {
      this.$socket.off("connect", this.onSocketConnect);
      this.$socket.off("chat:newMessage", this.onNewMessage);
      this.$socket.off("chat:messagesRead", this.onMessagesRead);
      this.$socket.off("chat:typing", this.onPartnerTyping);
      this.$socket.off("chat:stopTyping", this.onPartnerStopTyping);
      if (this.conversationId && this.$socket.connected) {
        this.$socket.emit("chat:leave", this.conversationId);
      }
    },

    reconnectSocketFallback() {
      const loginBy = this.$store.state.auth?.login_by || "";
      let fallbackRole = this.myRole;
      if (loginBy.includes("seller")) fallbackRole = "seller";
      else if (loginBy.includes("admin")) fallbackRole = "admin";

      import('@/plugins/socket').then(module => {
        if (fallbackRole === "seller") {
          const sellerId = this.$store.getters.sellerInfo?.sellerId || this.$store.getters.sellerInfo?.id;
          if (sellerId) module.connectUserSocket(sellerId, "seller");
        } else if (fallbackRole === "admin") {
          const id = this.$store.getters.userId || this.$store.getters.user?._id;
          if (id) module.connectUserSocket(id, "admin");
        } else {
          const id = this.$store.getters.userId || this.$store.getters.user?._id;
          if (id) module.connectUserSocket(id, "user");
        }
      });
    },

    onSocketConnect() {
      if (this.conversationId) {
        this.$socket.emit("chat:join", this.conversationId);
      }
    },

    onNewMessage({ conversationId, message }) {
      if (conversationId !== this.conversationId) return;
      // Avoid duplicate (from our own send)
      if (message.senderRole === this.myRole) return;

      this.messages.push(message);
      this.$nextTick(() => this.scrollToBottom());

      // Auto-mark as read since we're viewing
      this.markAsRead();
    },

    onMessagesRead({ conversationId, readBy }) {
      if (conversationId !== this.conversationId) return;
      // If the other party read our messages
      if (readBy !== this.myRole) {
        this.messages.forEach((msg) => {
          if (msg.senderRole === this.myRole && !msg.isRead) {
            msg.isRead = true;
          }
        });
      }
    },

    onPartnerTyping({ conversationId, senderRole }) {
      if (conversationId !== this.conversationId) return;
      if (!this.isTyping && senderRole !== this.myRole) {
        this.isTyping = true;
        this.$nextTick(() => this.scrollToBottom());
      }
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.isTyping = false;
      }, 3000);
    },

    onPartnerStopTyping({ conversationId }) {
      if (conversationId !== this.conversationId) return;
      this.isTyping = false;
    },

    onTyping() {
      if (!this.conversationId) return;
      
      if (this.isEmittingTyping) return;

      this.isEmittingTyping = true;
      this.$socket.emit("chat:typing", {
        conversationId: this.conversationId,
        senderRole: this.myRole,
      });

      // Throttle typing emissions to once per second
      setTimeout(() => {
        this.isEmittingTyping = false;
      }, 1000);
    },

    onScroll() {
      const area = this.$refs.messagesArea;
      if (!area) return;
      if (area.scrollTop <= 50 && this.hasMore && !this.loadingMore) {
        this.loadMessages(true);
      }
    },

    scrollToBottom() {
      const area = this.$refs.messagesArea;
      if (area) {
        area.scrollTop = area.scrollHeight;
      }
    },
  },
};
</script>

<style scoped>
.chat-window {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fafafa;
}

.chat-window__header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.chat-window__header-info {
  display: flex;
  flex-direction: column;
}

.chat-window__header-name {
  font-weight: 600;
  font-size: 15px;
  color: #1a1a2e;
}

.chat-window__typing {
  font-size: 12px;
  color: #667eea;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.chat-window__online {
  font-size: 12px;
  color: #4caf50;
}

.chat-window__offline {
  font-size: 12px;
  color: #999;
}

.chat-window__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
}

.chat-window__loading {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.chat-window__date-separator {
  display: flex;
  justify-content: center;
  margin: 16px 0 8px;
}

.chat-window__date-separator span {
  background: #e0e0e0;
  color: #666;
  font-size: 11px;
  padding: 4px 14px;
  border-radius: 12px;
}

.chat-window__bubble-wrap {
  display: contents;
}

.chat-window__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ccc;
}

.chat-window__empty p {
  margin-top: 8px;
  font-size: 14px;
}

.chat-window__input-area {
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #eee;
}

.chat-window__input >>> .v-input__slot {
  border-radius: 24px !important;
  background: #f5f5f5 !important;
}

/* Typing Indicator Bubble Styles */
.chat-window__typing-wrap {
  display: flex;
  margin-bottom: 8px;
  padding: 0 16px;
}

.chat-window__typing-bubble {
  padding: 12px 16px;
  border-radius: 18px;
  background: #f0f0f5;
  border-bottom-left-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.chat-window__typing-bubble span {
  display: block;
  width: 6px;
  height: 6px;
  background-color: #9ca3af;
  border-radius: 50%;
  animation: typingBounce 1.4s infinite ease-in-out both;
}

.chat-window__typing-bubble span:nth-child(1) {
  animation-delay: -0.32s;
}

.chat-window__typing-bubble span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typingBounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style>
